// InputTag Component

import React, { Component } from 'react'
import PropTypes from 'prop-types'

import {
  WithContext as ReactTags,
  WithOutContext as ReactTagsWithoutContext
} from 'react-tag-input'
import IntlUtil from '../../../lib/intlutil.jsx'
import IconButton from '../../../theme/common/icon_button/icon_button.jsx'

class InputTag extends Component {
  constructor (props) {
    super(props)

    this.state = {
      showSuggestions: false
    }

    this._references = {
      inputTag: React.createRef()
    }
  }

  formatInputTags (inputArray) {
    if (!inputArray) return []
    return inputArray.map(tag => {
      if (typeof tag === 'string') {
        return { id: tag, text: tag }
      }
      return { id: `${tag.id}` || tag.text, text: tag.text }
    })
  }

  filterSuggestionsByKeyword = (keyword, suggestions) => {
    if (!suggestions) return []

    let pattern
    try {
      pattern = new RegExp(keyword, 'gi')
    } catch {
      return []
    }

    return suggestions.filter(tag => pattern.test(tag.text))
  }

  filterSuggestionsByTags = suggestions => {
    const inputTags = this.formatInputTags(this.props.tags)
    return [...suggestions].filter(
      suggestion =>
        !inputTags.find(
          tag => tag.text.toLowerCase() === suggestion.text.toLowerCase()
        )
    )
  }

  handleFilterSuggestions = (keyword, suggestions) => {
    const suggestionTags = this.filterSuggestionsByTags(suggestions)

    return this.filterSuggestionsByKeyword(keyword, suggestionTags)
  }

  focus () {
    if (
      this._references.inputTag.current &&
      this._references.inputTag.current.resetAndFocusInput
    ) {
      this._references.inputTag.current.resetAndFocusInput()
    } else if (
      this._references.inputTag.current &&
      this._references.inputTag.current.ref &&
      this._references.inputTag.current.ref.current &&
      this._references.inputTag.current.ref.current.resetAndFocusInput
    ) {
      this._references.inputTag.current.ref.current.resetAndFocusInput()
    }
  }

  render () {
    const Element = this.props.withoutContext
      ? ReactTagsWithoutContext
      : ReactTags
    const className = `input-tag${this.props.bAlt ? ' alt' : ''}`

    const elementProps = {
      tags: this.formatInputTags(this.props.tags),
      suggestions: this.filterSuggestionsByTags(
        this.formatInputTags(this.props.tagSuggestions)
      ),
      handleDelete: this._handleDeleteTag.bind(this),
      handleAddition: this._handleAddTag.bind(this, false),
      handleInputBlur: this._handleAddTag.bind(this, true),
      handleInputFocus: this.props.onFocus,
      handleFilterSuggestions: this.handleFilterSuggestions,
      handleDrag: this._handleDragTag.bind(this),
      placeholder: this.props.placeholder
        ? IntlUtil.convertMessage(this.props.placeholder)
        : '',
      minQueryLength: 1,
      shouldRenderSuggestions: this.shouldRenderSuggestions,
      classNames: {
        tagInputField: 'form-input',
        tags: className,
        tag: 'input-tag-ovrly',
        remove: 'input-tag-remove'
      },
      ref: this._references.inputTag
    }

    if (this.props.delimiterKeys && this.props.delimiterKeys.length > 0) {
      elementProps.delimiters = this.props.delimiterKeys
    }

    if (!this.props.showOptionIcon) {
      return <Element {...elementProps} />
    }

    return (
      <div className='forminputtag'>
        <Element {...elementProps} />
        {this.props.tagSuggestions && this.props.tagSuggestions.length > 0 && (
          <IconButton
            icon='arrow-down'
            iconSize='12'
            className='actionexpand'
            onClick={this.handleToggleShowSuggestions}
          />
        )}
      </div>
    )
  }

  shouldRenderSuggestions = keyword => {
    return this.state.showSuggestions || keyword.length >= 1
  }

  handleToggleShowSuggestions = show => {
    if (typeof show === 'boolean') {
      this.setState({ showSuggestions: show })
    } else {
      this.setState(({ showSuggestions }) => ({
        showSuggestions: !showSuggestions
      }))
      this.focus()
    }
  }

  /* Private function */
  _handleDeleteTag (i) {
    const tags = [...this.props.tags]
    if (!this.props.hideDelete || tags.length > 1) {
      tags.splice(i, 1)

      if (this.props.onUpdateTags) {
        this.props.onUpdateTags(tags)
      }

      if (this.props.onDeleteTags) {
        this.props.onDeleteTags(tags)
      }
    }
  }

  _handleAddTag (bBlur, inputTag, _ev) {
    if (inputTag) {
      const isTagString = typeof inputTag === 'string'
      const tag = isTagString ? { id: inputTag, text: inputTag } : inputTag

      const tagExists = this.formatInputTags(this.props.tags).find(
        formattedTag =>
          formattedTag.text.toLowerCase() === tag.text.toLowerCase()
      )

      if (!tagExists) {
        if (
          this.props.tags &&
          this.props.tags[0] &&
          typeof this.props.tags[0] === 'string'
        ) {
          this.props.onUpdateTags([...this.props.tags, tag.text])
        } else {
          this.props.onUpdateTags([...this.props.tags, tag])
        }
      }
    }

    if (!bBlur) {
      this.handleToggleShowSuggestions(false)
    } else {
      if (this.props.onBlur) {
        this.props.onBlur()
      }
    }
  }

  _handleDragTag (tag, currPos, newPos) {
    const tags = [...this.props.tags]

    // mutate array
    tags.splice(currPos, 1)
    tags.splice(newPos, 0, tag)

    if (this.props.onUpdateTags) {
      this.props.onUpdateTags(tags)
    }
  }
}

InputTag.propTypes = {
  placeholder: PropTypes.string,
  tags: PropTypes.array,
  tagSuggestions: PropTypes.array,
  onUpdateTags: PropTypes.func,
  withoutContext: PropTypes.bool,
  bAlt: PropTypes.bool,
  showOptionIcon: PropTypes.bool,
  onDeleteTags: PropTypes.func,
  delimiterKeys: PropTypes.array,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func
}

InputTag.defaultProps = {
  placeholder: '',
  tags: [],
  tagSuggestions: [],
  onUpdateTags: null,
  withoutContext: false,
  bAlt: false,
  showOptionIcon: false,
  onDeleteTags: null,
  delimiterKeys: [],
  onFocus: null,
  onBlur: null
}

export default InputTag
